<template>
  <div class="ctc-results">
    <div class="ctc-results__meta">
      <span>
        {{ products.length }} résultat{{ products.length > 1 ? "s" : "" }} ·
        offre disponible pour ce site
      </span>
      <span v-if="pageCount > 1" class="ctc-results__pages">
        <button
          type="button"
          :disabled="page === 1"
          aria-label="Page précédente"
          @click="page -= 1"
        >
          <v-icon small>{{ icons.mdiChevronLeft }}</v-icon>
        </button>
        Page {{ page }} / {{ pageCount }}
        <button
          type="button"
          :disabled="page === pageCount"
          aria-label="Page suivante"
          @click="page += 1"
        >
          <v-icon small>{{ icons.mdiChevronRight }}</v-icon>
        </button>
      </span>
    </div>

    <ul class="ctc-results__grid" aria-label="Produits Affilizz trouvés">
      <li
        v-for="product in visible"
        :key="product.productLocalizedId"
        class="ctc-results__card"
        :class="{ 'ctc-results__card--added': isAdded(product) }"
      >
        <div class="ctc-results__thumb">
          <span class="ctc-results__tag">Affilizz</span>
          <span
            v-if="isAdded(product)"
            class="ctc-results__tag ctc-results__tag--added"
          >
            <v-icon x-small color="currentColor">{{ icons.mdiCheck }}</v-icon>
            Dans le widget
          </span>
          <img
            v-if="product.imageUrl"
            :src="product.imageUrl"
            alt=""
            width="96"
            height="78"
            loading="lazy"
          />
          <v-icon v-else large :color="colors.subtle">
            {{ icons.mdiImageOffOutline }}
          </v-icon>
        </div>
        <div class="ctc-results__body">
          <strong class="ctc-results__name" :title="product.title">
            {{ product.title }}
          </strong>
          <span class="ctc-results__price">{{ priceLabel(product) }}</span>
          <small class="ctc-results__offers">
            {{ offerLabel(product.offerCount) }}
          </small>
          <div class="ctc-results__actions">
            <span v-if="isAdded(product)" class="ctc-results__added">
              <v-icon x-small color="currentColor">{{ icons.mdiCheck }}</v-icon>
              Ajouté au widget
            </span>
            <template v-else>
              <rmr-action-button
                :label="primaryLabel"
                :icon="icons.mdiPlus"
                size="sm"
                :disabled="!canAddPrimary"
                class="ctc-results__add"
                @click="addPrimary(product)"
              />
              <v-menu
                :value="menuFor === product.productLocalizedId"
                top
                left
                offset-y
                nudge-top="6"
                content-class="ctc-results__menu-content"
                @input="menuFor = $event ? product.productLocalizedId : null"
              >
                <!-- The span is what v-menu measures; ARIA on the button. -->
                <template #activator="{ on }">
                  <span class="ctc-results__more" v-on="on">
                    <rmr-icon-button
                      :icon="icons.mdiChevronDown"
                      :disabled="!canAddProduct"
                      :aria-label="`Autres façons d’ajouter ${product.title}`"
                      aria-haspopup="menu"
                      :aria-expanded="
                        menuFor === product.productLocalizedId
                          ? 'true'
                          : 'false'
                      "
                    />
                  </span>
                </template>
                <v-list dense class="ctc-results__menu">
                  <template v-if="groups.length">
                    <p class="ctc-results__menu-label">
                      Ajouter comme coloris de
                    </p>
                    <v-list-item
                      v-for="(group, index) in groups"
                      :key="index"
                      class="ctc-results__menu-item"
                      @click="$emit('add', { product, groupIndex: index })"
                    >
                      <span class="ctc-results__menu-num">{{ index + 1 }}</span>
                      <span class="ctc-results__menu-text" :title="group.title">
                        {{ shortTitle(group.title) }}
                      </span>
                    </v-list-item>
                    <p class="ctc-results__menu-label">Ou</p>
                  </template>
                  <v-list-item
                    :disabled="!canAddModel"
                    class="ctc-results__menu-item"
                    @click="$emit('add', { product, groupIndex: null })"
                  >
                    <span
                      class="ctc-results__menu-num ctc-results__menu-num--new"
                    >
                      <v-icon x-small color="currentColor">
                        {{ icons.mdiPlus }}
                      </v-icon>
                    </span>
                    <span class="ctc-results__menu-text">
                      Nouveau modèle{{
                        widgetType === "BOX" ? " (onglet)" : ""
                      }}
                    </span>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { computed, ref, watch } from "@vue/composition-api";
import {
  mdiCheck,
  mdiChevronDown,
  mdiChevronLeft,
  mdiChevronRight,
  mdiImageOffOutline,
  mdiPlus,
} from "@mdi/js";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrIconButton from "@/components/ui/actions/RmrIconButton.vue";
import { RMR_TEXT_SUBTLE } from "@/styles/tokens";

import { offerCountLabel, similarPriceLabel } from "../ctc-model";

const PAGE_SIZE = 6;

/**
 * Search results of the widget composer. The main action adds to the active
 * model (as a colour); its menu adds to another model or starts a new one.
 */
export default {
  name: "CtcWidgetResults",
  components: { RmrActionButton, RmrIconButton },
  props: {
    products: {
      type: Array,
      default: () => [],
    },
    selectedIds: {
      type: Array,
      default: () => [],
    },
    // useCtcCreateFlow().state.similar.selected, read only.
    groups: {
      type: Array,
      default: () => [],
    },
    activeIndex: {
      type: Number,
      default: 0,
    },
    widgetType: {
      type: String,
      default: "BOX",
    },
    canAddProduct: {
      type: Boolean,
      default: true,
    },
    canAddModel: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, { emit }) {
    const page = ref(1);
    const menuFor = ref(null);
    const pageCount = computed(() =>
      Math.max(1, Math.ceil(props.products.length / PAGE_SIZE))
    );
    watch(
      () => props.products,
      () => {
        page.value = 1;
      }
    );
    const activeExists = computed(() => !!props.groups[props.activeIndex]);

    return {
      // No model yet: the first result starts one.
      addPrimary: (product) =>
        emit("add", {
          product,
          groupIndex: activeExists.value ? props.activeIndex : null,
        }),
      canAddPrimary: computed(() =>
        activeExists.value ? props.canAddProduct : props.canAddModel
      ),
      colors: { subtle: RMR_TEXT_SUBTLE },
      icons: {
        mdiCheck,
        mdiChevronDown,
        mdiChevronLeft,
        mdiChevronRight,
        mdiImageOffOutline,
        mdiPlus,
      },
      isAdded: (product) =>
        props.selectedIds.includes(product.productLocalizedId),
      menuFor,
      offerLabel: offerCountLabel,
      shortTitle: (title) => {
        const text = (title || "").trim() || "Sans titre";
        return text.length > 28 ? `${text.slice(0, 28)}…` : text;
      },
      page,
      pageCount,
      priceLabel: similarPriceLabel,
      primaryLabel: computed(() =>
        activeExists.value
          ? `Ajouter à Modèle ${props.activeIndex + 1}`
          : "Nouveau modèle"
      ),
      visible: computed(() =>
        props.products.slice(
          (page.value - 1) * PAGE_SIZE,
          page.value * PAGE_SIZE
        )
      ),
    };
  },
};
</script>

<style lang="scss" scoped>
.ctc-results__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 10px 0 12px;
  color: $rmr-text-muted;
  font-size: $rmr-fs-md;
}

.ctc-results__pages {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;

  button {
    display: inline-flex;
    width: 26px;
    height: 26px;
    align-items: center;
    justify-content: center;
    border: 1px solid $rmr-border-hover;
    border-radius: $rmr-radius-sm;
    background: $rmr-surface-0;

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    &:focus-visible {
      outline: 2px solid $rmr-indigo;
      outline-offset: 2px;
    }
  }
}

.ctc-results__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.ctc-results__card {
  display: flex;
  min-width: 0;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-lg;
  background: $rmr-surface-0;
}

.ctc-results__card--added {
  border-color: rgba($rmr-green, 0.45);
}

.ctc-results__thumb {
  position: relative;
  display: grid;
  height: 118px;
  place-items: center;
  background: $rmr-surface-2;

  img {
    width: auto;
    max-width: 70%;
    height: 78px;
    object-fit: contain;
  }
}

.ctc-results__tag {
  position: absolute;
  top: 8px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 7px;
  border-radius: $rmr-radius-xs;
  background: $rmr-surface-0;
  color: $rmr-indigo;
  font-size: $rmr-fs-sm;
  font-weight: 700;
}

.ctc-results__tag--added {
  right: 8px;
  left: auto;
  background: $rmr-green;
  color: $rmr-surface-0;
}

.ctc-results__body {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px 12px;
}

.ctc-results__name {
  display: -webkit-box;
  min-height: 2.7em;
  overflow: hidden;
  color: $rmr-text-primary;
  font-size: $rmr-fs-body;
  font-weight: 600;
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.ctc-results__price {
  color: $rmr-text-primary;
  font-size: $rmr-fs-stat;
  font-weight: 700;
}

.ctc-results__offers {
  color: $rmr-text-muted;
  font-size: $rmr-fs-base;
}

.ctc-results__actions {
  display: flex;
  min-height: 34px;
  align-items: center;
  gap: 6px;
  margin-top: auto;
  padding-top: 8px;
}

.ctc-results__add {
  min-width: 0;
  flex: 1;
}

// The ▾ menu: one quiet panel, numbered models, a hover tint.
.ctc-results__menu {
  width: 240px;
  padding: 6px !important;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-md !important;
  background: $rmr-surface-0 !important;
}

.ctc-results__menu-label {
  margin: 0;
  padding: 6px 8px 4px;
  color: $rmr-text-subtle;
  font-size: $rmr-fs-sm;
  font-weight: 700;
}

.ctc-results__menu-item {
  min-height: 34px !important;
  gap: 8px;
  padding: 0 8px !important;
  border-radius: $rmr-radius-sm;
  color: $rmr-text-primary !important;
  font-size: $rmr-fs-body;

  &::before {
    border-radius: $rmr-radius-sm;
  }

  &:hover,
  &:focus-visible {
    background: $rmr-indigo-bg;
  }

  &.v-list-item--disabled {
    opacity: 0.45;
  }
}

.ctc-results__menu-num {
  display: grid;
  width: 22px;
  height: 22px;
  flex: none;
  place-items: center;
  border-radius: $rmr-radius-sm;
  background: $rmr-indigo-bg;
  color: $rmr-indigo;
  font-size: $rmr-fs-md;
  font-weight: 700;
}

.ctc-results__menu-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// Sentence case like the rest of the step, not Vuetify's capitals.
.ctc-results__actions ::v-deep .v-btn {
  letter-spacing: 0 !important;
  text-transform: none !important;
}

.ctc-results__more {
  display: inline-flex;
}

.ctc-results__added {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: $rmr-green-dark;
  font-size: $rmr-fs-md;
  font-weight: 600;
}

@media (max-width: 1180px) {
  .ctc-results__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 480px) {
  .ctc-results__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>

<style lang="scss">
// v-menu renders its wrapper outside this component: let the inner panel
// carry the look (border, radius, shadow).
.ctc-results__menu-content {
  border-radius: $rmr-radius-md !important;
  box-shadow: 0 12px 30px rgba($rmr-text-primary, 0.14) !important;
}
</style>
